/** Purchase requests. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enStokRequests = {
  // ---- Status labels (badges; text carries the meaning)
  'stok.pr.status.draft': 'Draft',
  'stok.pr.status.pending_approval': 'Awaiting approval',
  'stok.pr.status.approved': 'Approved',
  'stok.pr.status.rejected': 'Rejected',
  'stok.pr.status.cancelled': 'Cancelled',
  'stok.pr.status.converted': 'Turned into PO',

  // ---- List
  'stok.pr.title': 'Purchase requests',
  'stok.pr.subtitle': 'What the kitchen and store need bought, newest first.',
  'stok.pr.create': 'New request',
  'stok.pr.readOnly': 'Read only — creating requests needs the purchase request permission.',
  'stok.pr.noPermission': 'You do not have access to purchase requests in this branch.',
  'stok.pr.noRequest': 'Ask a user with the purchase request permission to create the request.',
  'stok.pr.listTitle': 'Requests',
  'stok.pr.listDesc': 'Open a request to see its lines, approval steps and purchase order.',
  'stok.pr.statusFilter': 'Filter by status',
  'stok.pr.searchPlaceholder': 'Request number, requester or budget',
  'stok.pr.empty': 'No purchase requests yet — create one for what needs to be bought.',
  'stok.pr.emptyReadOnly': 'No purchase requests yet — they appear once someone creates one.',
  'stok.pr.emptyFiltered': 'No requests with this status.',
  'stok.pr.number': 'Request no.',
  'stok.pr.requestedBy': 'Requested by',
  'stok.pr.requestedByName': 'Requested by {name}',
  'stok.pr.allRequesters': 'All requesters',
  'stok.pr.neededBy': 'Needed by',
  'stok.pr.total': 'Estimated total',

  // ---- Budget line (shared with purchase orders)
  'stok.pr.budget.column': 'Budget line',
  'stok.pr.budget.label': 'Budget line',
  'stok.pr.budget.hint': 'Approved lines of {period}; the amount is committed when approved.',
  'stok.pr.budget.none': 'No budget',
  'stok.pr.budget.option': '{label} — {available} available',
  'stok.pr.budget.saved': 'Saved budget line',
  'stok.pr.budget.loading': 'Loading budget lines…',
  'stok.pr.budget.loadFailed': 'Budget lines could not be loaded — save without a budget or retry.',
  'stok.pr.budget.empty': 'No approved budget lines for {period} yet.',
  'stok.pr.budget.otherPeriod': 'The saved line is not an approved line of this month.',
  'stok.pr.budget.available': 'Available',
  'stok.pr.budget.over': 'The total {total} is above the {available} still available.',
  'stok.pr.budget.overBlock': 'Submitting will be refused by this budget line.',
  'stok.pr.budget.overEscalate': 'Submitting will need an extra approval (escalation).',

  // ---- Form
  'stok.pr.backToList': 'All purchase requests',
  'stok.pr.newTitle': 'New purchase request',
  'stok.pr.newSubtitle': 'Saved as a draft; submit it for approval when it is complete.',
  'stok.pr.detailTitle': 'Purchase request',
  'stok.pr.editTitle': 'Edit draft {number}',
  'stok.pr.editSubtitle': 'Saving replaces every line of the draft.',
  'stok.pr.headTitle': 'Request details',
  'stok.pr.headDesc': 'Budget line, when the goods are needed and why.',
  'stok.pr.notesHint': 'Why it is needed, e.g. weekend event or low stock.',
  'stok.pr.linesFormDesc':
    'Quantity in the chosen unit, estimated price per one unit before tax. At most {max} lines.',
  'stok.pr.estPriceLabel': 'Estimated price per {unit} ({currency})',
  'stok.pr.suggestedSupplier': 'Suggested supplier',
  'stok.pr.noSupplier': 'No suggestion',
  'stok.pr.suppliersHidden':
    'The supplier list could not be loaded, so no supplier can be suggested. The request can still be saved — purchasing chooses the supplier.',
  'stok.pr.draftNote': 'Drafts can still be changed until they are submitted.',
  'stok.pr.created': 'Purchase request {number} saved as a draft.',
  'stok.pr.saved': 'Purchase request {number} saved.',
  'stok.pr.err.tooManyLines': 'A request can have at most 100 lines.',

  // ---- Detail
  'stok.pr.factsTitle': 'Request',
  'stok.pr.factsDesc': 'Who asked for it, when it is needed and which budget it uses.',
  'stok.pr.linesDesc': 'Quantities in the unit of each line; prices are estimates.',
  'stok.pr.estPrice': 'Estimated price',
  'stok.pr.supplierHidden': 'Suggested',
  'stok.pr.purchaseOrder': 'Purchase order',
  'stok.pr.openPo': 'Open the purchase order',
  'stok.pr.cancelReason': 'Cancellation reason',
  'stok.pr.note.draft': 'Draft — submit it for approval when it is complete.',
  'stok.pr.note.draftReadOnly': 'Draft — the requester submits it for approval.',
  'stok.pr.note.pending': 'Waiting for approval — see the approval steps below.',
  'stok.pr.note.approved': 'Approved — purchasing turns it into a purchase order.',
  'stok.pr.note.approvedManage':
    'Approved — create the purchase order with the supplier and negotiated prices.',
  'stok.pr.note.rejected':
    'Rejected — see the approver’s reason below; create a new request if it is still needed.',
  'stok.pr.budgetExceededHelp':
    'Choose another budget line or lower the quantities or prices, then submit again.',
  'stok.pr.editDraft': 'Edit draft',

  // ---- Submit / cancel
  'stok.pr.submit': 'Submit for approval',
  'stok.pr.submitting': 'Submitting…',
  'stok.pr.submitTitle': 'Submit request {number}?',
  'stok.pr.submitBody': 'The request can no longer be edited while it waits for approval.',
  'stok.pr.submitBudget':
    'The budget line is checked now; the amount is committed when the request is approved.',
  'stok.pr.submitNoBudget': 'No budget line is selected — no budget control applies.',
  'stok.pr.submitted': 'Purchase request {number} submitted.',
  'stok.pr.cancel': 'Cancel request',
  'stok.pr.cancelling': 'Cancelling…',
  'stok.pr.cancelTitle': 'Cancel request {number}?',
  'stok.pr.cancelBody': 'The draft is closed and cannot be submitted anymore.',
  'stok.pr.cancelBodyPending': 'The open approval is withdrawn.',
  'stok.pr.cancelBodyApproved': 'The amount committed on the budget line is released.',
  'stok.pr.cancelled': 'Purchase request {number} cancelled.',

  // ---- Convert to a purchase order
  'stok.pr.convert': 'Create PO',
  'stok.pr.converting': 'Creating…',
  'stok.pr.convertTitle': 'Create a purchase order from {number}',
  'stok.pr.convertDesc': 'Choose the supplier and confirm the negotiated price of each line.',
  'stok.pr.convertPrice': 'Price of {item} per {unit} ({currency})',
  'stok.pr.estimate': 'estimate',
  'stok.pr.poTotal': 'Purchase order total',
  'stok.pr.approvedTotal': 'Approved request total',
  'stok.pr.convertWithin':
    'Within the approved total — the order is approved at once (it inherits this approval).',
  'stok.pr.convertAbove':
    'Above the approved total — the order is created as a draft and must be submitted for approval.',
  'stok.pr.convertRule':
    'Up to the approved total the order inherits the request’s approval; above it, the full amount needs a new approval.',
  'stok.pr.convertedApproved': 'Purchase order {number} created and approved.',
  'stok.pr.convertedDraft': 'Purchase order {number} created as a draft — submit it for approval.',
} as const;
